import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * useFilterParams – keeps filter state in URL search params so the
 * browser back-button restores filters after navigating away.
 *
 * @param {Object} defaultFilters – e.g. { search: "", status: "", sort: "recent" }
 * @returns {[Object, (key: string, value: string) => void, () => void]}
 *   [filters, setFilter, clearAll]
 */
export default function useFilterParams(defaultFilters) {
    const [searchParams, setSearchParams] = useSearchParams();

    // Build the current filters object from URL params, falling back to defaults
    const filters = useMemo(() => {
        const result = {};
        for (const key of Object.keys(defaultFilters)) {
            result[key] = searchParams.get(key) || defaultFilters[key];
        }
        return result;
    }, [searchParams, defaultFilters]);

    // Update a single filter key. Removes the param if it matches the default.
    const setFilter = useCallback(
        (key, value) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    if (value === defaultFilters[key]) {
                        next.delete(key);
                    } else {
                        next.set(key, value);
                    }
                    return next;
                },
                { replace: true }
            );
        },
        [setSearchParams, defaultFilters]
    );

    // Clear all filters back to defaults
    const clearAll = useCallback(() => {
        setSearchParams({}, { replace: true });
    }, [setSearchParams]);

    return [filters, setFilter, clearAll];
}
